import { Equipment } from "domain/entities/equipment";
import { Message } from "domain/entities/message";
import { parseEquipmentAttributes } from "presentation/components/Equipment/equipment-utils";
import { useLogVehicleMileageMutation, useUpdateMyEquipmentAttributesMutation } from "presentation/hooks/use-query-equipments";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { confirmDialog } from "primereact/confirmdialog";
import MobileBottomSheet from "presentation/mobile/components/MobileBottomSheet";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { RefObject, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getApiErrorMessage } from "presentation/mobile/utils/api-error.util";

interface RegisterKMsProps {
    visible: boolean;
    setIsVisible: (visible: boolean) => void;
    equipment: Equipment | null;
    setEquipment: (equipment: Equipment | null) => void;
    toastRef: RefObject<Toast | null>
    mileageLogsData: Message<any[]> | undefined
}

const RegisterKMs = ({
    visible,
    setIsVisible,
    equipment,
    setEquipment,
    toastRef,
    mileageLogsData
}: RegisterKMsProps) => {

    const { mutateAsync: updateAttributesMutation } = useUpdateMyEquipmentAttributesMutation();
    const { mutateAsync: logMileageMutation } = useLogVehicleMileageMutation();
    const queryClient = useQueryClient();

    const attributesObject = useMemo(() => {
        if (!equipment?.attributes)
            return "";
        return parseEquipmentAttributes(equipment.attributes)
    }, [equipment?.attributes])

    const [mileageData, setMileageData] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        kms: 0,
    });
    const [isSaving, setIsSaving] = useState(false);

    const cleanUpAndExit = () => {

        setMileageData({
            month: new Date().getMonth() + 1,
            year: new Date().getFullYear(),
            kms: 0,
        });

        setEquipment(null);
        setIsVisible(false);
    }

    // The user can't close mid-save; cleanUpAndExit itself is still used after a successful save
    const handleClose = () => {
        if (isSaving) return;
        cleanUpAndExit();
    }

    const executeSaveMileage = async () => {
        if (!equipment?.id || isSaving) return;
        const equipmentId = equipment.id;

        setIsSaving(true);
        try {

            await logMileageMutation({
                id: equipmentId,
                month: mileageData.month,
                year: mileageData.year,
                kms: mileageData.kms,
            });

            await queryClient.invalidateQueries({
                queryKey: ["vehicleMileageLogs", equipmentId],
            });

            toastRef.current?.show({
                severity: "success",
                summary: "Sucesso",
                detail: "KMs registado.",
                life: 3000,
            });
            cleanUpAndExit();
        } catch (err: unknown) {
            toastRef.current?.show({
                severity: "error",
                summary: "Erro",
                detail: getApiErrorMessage(err, "Erro ao registar KMs."),
                life: 3500,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveMileage = async () => {
        if (!equipment?.id || isSaving) return;

        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;

        if (
            mileageData.year > currentYear ||
            (mileageData.year === currentYear && mileageData.month > currentMonth)
        ) {
            toastRef.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Não é permitido registar kms para meses futuros.",
                life: 3000,
            });
            return;
        }

        const logs = mileageLogsData?.data || [];

        const futureLogs = logs.filter(log => log.year > mileageData.year || (log.year === mileageData.year && log.month > mileageData.month));
        if (futureLogs.length > 0) {
            futureLogs.sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);
            const closestFutureLog = futureLogs[0];
            if (mileageData.kms > closestFutureLog.kms) {
                toastRef.current?.show({
                    severity: "error",
                    summary: "Valor Inválido",
                    detail: `Os KMs não podem ser superiores ao registo seguinte (${closestFutureLog.kms} km em ${closestFutureLog.month}/${closestFutureLog.year}).`,
                });
                return;
            }
        }

        // Verificar se já existe log para o mesmo mês/ano
        const existingLog = mileageLogsData?.data?.find(
            (log) => log.month === mileageData.month && log.year === mileageData.year,
        );

        if (existingLog) {
            confirmDialog({
                message: `Já existe um registo de KMs de ${existingLog.kms.toLocaleString()} km para ${mileageData.month.toString().padStart(2, "0")}/${mileageData.year}. Deseja atualizar o valor para ${mileageData.kms.toLocaleString()} km?`,
                header: "Registo de KMs Existente",
                icon: "pi pi-exclamation-triangle",
                acceptLabel: "Sim, Atualizar",
                rejectLabel: "Não",
                accept: async () => { await executeSaveMileage() },
            });
        } else {
            await executeSaveMileage();
        }
    };

    const footer = (
        <div className="w-full flex justify-content-center align-items-center gap-3">
            <Button
                label="Cancelar"
                type="button"
                className="btn-mobile-secondary"
                style={{ width: "135px", height: "42px" }}
                disabled={isSaving}
                onClick={handleClose}
            />
            <Button
                label="Guardar"
                icon="pi pi-check"
                type="button"
                className="btn-mobile"
                style={{ width: "135px", height: "42px" }}
                loading={isSaving}
                onClick={handleSaveMileage}
            />

        </div>
    );

    return (
        <MobileBottomSheet
            visible={visible}
            onHide={handleClose}
            title="Registar KMs"
            headerIcon="pi pi-gauge"
            footer={footer}
        >
            <div className="flex flex-column gap-3 py-2">
                <div>
                    <label htmlFor="mileageDate" className="text-xs font-semibold text-700 mb-1 block">
                        Período (Mês/Ano)
                    </label>
                    <Calendar
                        id="mileageDate"
                        value={new Date(mileageData.year, mileageData.month - 1, 1)}
                        onChange={(e) => {
                            if (e.value instanceof Date) {
                                setMileageData({
                                    ...mileageData,
                                    month: e.value.getMonth() + 1,
                                    year: e.value.getFullYear(),
                                });
                            }
                        }}
                        view="month"
                        dateFormat="mm/yy"
                        maxDate={new Date()}
                        className="w-full"
                        showIcon
                    />
                </div>
                <div>
                    <label htmlFor="kms" className="text-xs font-semibold text-700 mb-1 block">
                        KMs Atuais
                    </label>
                    <InputText
                        id="kms"
                        type="number"
                        value={mileageData.kms.toString()}
                        onChange={(e) =>
                            setMileageData({
                                ...mileageData,
                                kms: Number(e.target.value),
                            })
                        }
                        className="w-full"
                        placeholder="Ex: 85400"
                    />
                </div>
            </div>
        </MobileBottomSheet>
    );
}

export default RegisterKMs;